import {
  ArrowUpRight,
  Calculator,
  Check,
  Cpu,
  Github,
  MonitorPlay,
  RadioTower,
  Search,
  VolumeX,
  Wifi,
} from 'lucide-react';

function Logo() {
  return (
    <span className="brand" aria-label="CasioYT">
      <span className="brand-icon" aria-hidden="true"><Calculator size={19} strokeWidth={1.8} /></span>
      <span>Casio<span>YT</span></span>
    </span>
  );
}

const steps = [
  {
    icon: Wifi,
    title: 'Conecta la ESP32 al Wi-Fi',
    copy: 'Usa CasioWIFI una sola vez. La red queda guardada en la propia XIAO ESP32-C3.',
  },
  {
    icon: Search,
    title: 'Busca desde la calculadora',
    copy: 'Escribe con el teclado de la fx-CG50. Los resultados y sus miniaturas llegan directamente al add-in.',
  },
  {
    icon: MonitorPlay,
    title: 'Reproduce sin ordenador',
    copy: 'La ESP32 solicita, recibe y transmite los frames por UART. Esta página puede permanecer cerrada.',
  },
];

function FlowStep({ icon: Icon, title, copy, index }) {
  return (
    <article className="flow-step">
      <span className="flow-index">0{index + 1}</span>
      <span className="flow-icon"><Icon size={20} strokeWidth={1.7} /></span>
      <div>
        <h3>{title}</h3>
        <p>{copy}</p>
      </div>
    </article>
  );
}

export default function App() {
  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand-link" href="/tools/casio/casioyt/studio/" aria-label="Inicio de CasioYT">
          <Logo />
        </a>
        <nav aria-label="Navegación principal">
          <a href="#funcionamiento">Cómo funciona</a>
          <a href="https://github.com/samilososami/CasioYT" target="_blank" rel="noreferrer">
            <Github size={16} /> GitHub
          </a>
        </nav>
      </header>

      <main>
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-copy">
            <div className="status-label"><span /> Sistema autónomo</div>
            <h1 id="hero-title">YouTube, directamente desde tu Casio.</h1>
            <p className="hero-lead">
              Busca vídeos, recorre los resultados y reproduce desde la fx-CG50. Sin conectar la ESP32 al ordenador,
              sin Web Serial y sin mantener esta página abierta.
            </p>
            <div className="hero-actions">
              <a className="primary-button" href="https://github.com/samilososami/CasioYT/releases" target="_blank" rel="noreferrer">
                Descargar CasioYT <ArrowUpRight size={16} />
              </a>
              <a className="text-link" href="#funcionamiento">Ver funcionamiento</a>
            </div>
            <div className="autonomy-note">
              <Check size={16} strokeWidth={2.4} />
              <span><strong>La web no forma parte de la reproducción.</strong> El servicio vive en la nube y la ESP32 se comunica con él por HTTPS.</span>
            </div>
          </div>

          <div className="device-stage" aria-label="Casio fx-CG50 ejecutando CasioYT">
            <div className="device-halo" />
            <img src={`${import.meta.env.BASE_URL}assets/fx-cg50-player.png`} alt="Casio fx-CG50 con la interfaz de CasioYT" />
            <div className="device-status"><span /><div><strong>Conexión directa</strong><small>Casio → ESP32 → samilososami.com</small></div></div>
          </div>
        </section>

        <section className="flow" id="funcionamiento" aria-labelledby="flow-title">
          <div className="section-heading">
            <span>Funcionamiento</span>
            <h2 id="flow-title">Tres pasos. Ningún puente abierto.</h2>
            <p>Una vez instalado el firmware, todo el flujo se controla desde el teclado de la calculadora.</p>
          </div>
          <div className="flow-list">
            {steps.map((step, index) => <FlowStep key={step.title} {...step} index={index} />)}
          </div>
        </section>

        <section className="architecture" aria-labelledby="architecture-title">
          <div className="architecture-copy">
            <span>Arquitectura</span>
            <h2 id="architecture-title">El trabajo pesado ocurre fuera de la calculadora.</h2>
            <p>
              La API busca el vídeo y prepara una secuencia muy ligera. La ESP32 la descarga por Wi-Fi y la entrega
              por UART; la fx-CG50 únicamente valida, descomprime y dibuja cada frame.
            </p>
          </div>
          <div className="pipeline" aria-label="Ruta de datos de CasioYT">
            <div><Calculator size={22} /><strong>fx-CG50</strong><small>Búsqueda y controles</small></div>
            <i aria-hidden="true">→</i>
            <div><RadioTower size={22} /><strong>ESP32-C3</strong><small>Wi-Fi y UART</small></div>
            <i aria-hidden="true">→</i>
            <div><Cpu size={22} /><strong>CasioYT Cloud</strong><small>Conversión de frames</small></div>
          </div>
          <div className="spec-row">
            <span><strong>128 × 72</strong><small>stream interno</small></span>
            <span><strong>5 FPS</strong><small>objetivo inicial</small></span>
            <span><strong>115200</strong><small>baudios UART</small></span>
            <span><VolumeX size={18} /><strong>Sin audio</strong><small>prioridad a imagen</small></span>
          </div>
        </section>
      </main>

      <footer className="footer">
        <span>CasioYT · proyecto experimental para Casio fx-CG50</span>
        <a href="https://samilososami.com">samilososami.com</a>
      </footer>
    </div>
  );
}
